import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import "./ClientDetailMain.css";
import { Button, FormControl, MenuItem, Select } from "@mui/material";
import OnboardingFlow from "../OnboardingFlow/OnboardingFlow";
import axios from "axios";
import ClientEditCard from "../ClientEditcard/ClientEditCard";
import { API_URL } from "../../utils/auth";
import Auth from '../../utils/auth';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { CircularProgressbar, CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import InfoTooltip from './InfoTooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { formatDate } from '../../utils/dateFormat';

// Radial Separators Component for Retention Score Chart
const RadialSeparators = ({ count, style, circleRatio }) => {
    const turns = circleRatio / count;
    const offset = 0.5;

    return Array.from({ length: count }, (_, index) => (
        <div
            key={index}
            style={{
                position: "absolute",
                height: "100%",
                transform: `rotate(${(index * turns) - 0.25}turn)`
            }}
        >
            <div style={style} />
        </div>
    ));
};

const normalizeTouchpointFrequency = (value) => {
  if (!value) return "";
  const normalized = value.toLowerCase().trim();
  if (normalized === "weekly") return "Weekly";
  if (normalized === "bi-weekly" || normalized === "biweekly") return "Bi-Weekly";
  if (normalized === "monthly") return "Monthly";

  return "";
};

const DateField = ({ label, value, onChange, caption }) => (
  <div className={`date-field-container${caption ? ' date-field-container--with-caption' : ''}`}>
    <div className="date-field-row">
      <h2 className="date-field-label">{label}:</h2>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={value ? dayjs(value) : null}
          onChange={onChange}
          format="MMM DD, YYYY"
          className="date-picker-field"
        />
      </LocalizationProvider>
    </div>
    {caption && (
      <p className="date-field-caption">{caption}</p>
    )}
  </div>
);

const ClientDetailMain = ({ clientId }) => {
    const [client, setClient] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        address: "",
        onboardingDate: "",
        office: "",
        netPromoterScore: "",
        clientEffortScores: "",
        billingStatus: "",
        lifeTimeValue: "",
        icp: "",
        touchpointFrequency: "",
        companyId: "",
        archived: "",
        companyName: "",
        userTag: "",
        clientRetentionScore: 0,
        lastContacted: null,
        activityScore: null,
        monthlyFeeSubscription: "",
        oneTimeOnboardingFee: ""
    });
    const [onboardingProgress, setOnboardingProgress] = useState("0%");
    const [isSending, setIsSending] = useState(false);
    const navigate = useNavigate();
    const workspaceId = Auth.getWorkspaceId();
    const userId = Auth.getUser()?.id;
    const user = Auth.getUser();
    const isAdmin = user?.admin || false;
    const [allPrograms, setAllPrograms] = useState([]); 
    const [selectedPrograms, setSelectedPrograms] = useState([]); 
    const [programToRemove, setProgramToRemove] = useState(null); 
    const [openRemoveModal, setOpenRemoveModal] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [circleConnected, setCircleConnected] = useState(false);
    const [isMetricsLoading, setIsMetricsLoading] = useState(true);
    const [ghlSynced, setGhlSynced] = useState(false);

    useEffect(() => {
        const fetchClientData = async () => {
            if (!clientId || !workspaceId) return;
            try {
                const response = await axios.get(`${API_URL}/clients/get-client-by-id`, {
                    params: {
                        clientId: clientId,
                        workspaceId: workspaceId,
                        includeCompany: true
                    }
                });
                const clientData = response.data;
                
                setClient(clientData);
                const ghlSyncedValue = clientData.ghlSynced === true || clientData.ghlSynced === 'true' || clientData.ghlSynced === 1;
                setGhlSynced(ghlSyncedValue);
                setFormData({
                    firstName: clientData.firstName || "",
                    lastName: clientData.lastName || "",
                    email: clientData.email || "",
                    phoneNumber: clientData.phoneNumber || "",
                    address: clientData.address || "",
                    onboardingDate: clientData.onboardingDate || "",
                    office: clientData.office || "",
                    netPromoterScore: clientData.netPromoterScore || "",
                    clientEffortScores: clientData.clientEffortScores || "",
                    billingStatus: clientData.billingStatus ?? "",
                    lifeTimeValue: clientData.lifeTimeValue || "",
                    icp: clientData.icp ?? "",
                    touchpointFrequency: normalizeTouchpointFrequency(clientData.touchpointFrequency),
                    companyId: clientData.companyId || "",
                    archived: clientData.archived || "",
                    companyName: clientData.companyName || "",
                    userTag: clientData.userTag || "",
                    clientRetentionScore: clientData.clientRetentionScore || 0,
                    lastContacted: clientData.lastContacted ? new Date(clientData.lastContacted) : null,
                    lastCallAttended: clientData.lastCallAttended ? new Date(clientData.lastCallAttended) : null,
                    lastInPersonEvent: clientData.lastInPersonEvent ? new Date(clientData.lastInPersonEvent) : null,
                    activityScore: clientData.activityScore || null,
                    monthlyFeeSubscription: clientData.monthlyFeeSubscription || "",
                    oneTimeOnboardingFee: clientData.oneTimeOnboardingFee || ""
                });
                
                // Set selected programs from clientData.programs
                setSelectedPrograms((clientData.programs || []).map(p => p.id));
            } catch (error) {
                console.error("Error fetching client data:", error);
                setClient(null);
            }
        };
        fetchClientData();
    }, [clientId, workspaceId]);

    // GHL sync status is now included in the client data from get-client-by-id
    // No need for a separate API call

    // Fetch all available programs
    useEffect(() => {
        const fetchPrograms = async () => {
            if (!workspaceId || !userId) return;
            try {
                const { data } = await axios.get(`${API_URL}/programs`, { params: { workspaceId, userId } });
                setAllPrograms(data);
            } catch (err) {
                setAllPrograms([]);
            }
        };

        const checkCircleConnection = async () => {
            if (!workspaceId || !userId) return;
            try {
                const circleResponse = await axios.get(
                    `${API_URL}/circle/circle-token?workspaceId=${workspaceId}&userId=${userId}`
                );
                setCircleConnected(circleResponse.data.hasBotToken || false);
            } catch (err) {
                console.error("Failed to fetch Circle.so integration status:", err);
                setCircleConnected(false);
            } finally {
                setIsMetricsLoading(false);
            }
        };

        fetchPrograms();
        checkCircleConnection();
    }, [workspaceId, userId]);

    const handleProgramsChange = async (event) => {
        const value = event.target.value;
        const removed = selectedPrograms.find(id => !value.includes(id));
        const added = value.find(id => !selectedPrograms.includes(id));
        if (removed) {
            await handleRemoveProgram(removed);
            return;
        }
        if (added) {
            try {
                await axios.post(`${API_URL}/programs/assign-program`, {
                    workspaceId: Number(workspaceId),
                    userId: Number(userId),
                    programId: Number(added),
                    clientId: Number(clientId)
                });
                setSelectedPrograms(value);
                toast.success('Program assigned successfully!');
            } catch (err) {
                alert('Error assigning program');
            }
        }
    };

    const handleRemoveProgram = async (programId) => {
        try {
            await axios.delete(`${API_URL}/programs/remove-program`, {
                params: {
                    workspaceId: Number(workspaceId),
                    userId: Number(userId),
                    programId: Number(programId),
                    clientId: Number(clientId)
                }
            });
            setSelectedPrograms(prev => prev.filter(id => id !== programId));
            toast.success('Program removed successfully!');
        } catch (err) {
            console.error('Error removing program:', err, err?.response?.data);
            alert('Error removing program');
        }
    };

    const refreshClientData = async () => {
        try {
            const response = await axios.get(`${API_URL}/clients/get-client-by-id`, {
                params: {
                    clientId: clientId,
                    workspaceId: workspaceId,
                    includeCompany: true
                }
            });
            const updatedClient = response.data;
            setClient(updatedClient);
            const ghlSyncedValue = updatedClient.ghlSynced === true || updatedClient.ghlSynced === 'true' || updatedClient.ghlSynced === 1;
            setGhlSynced(ghlSyncedValue);
            setFormData(prev => ({
                ...prev,
                ...updatedClient,
                lastContacted: updatedClient.lastContacted ? new Date(updatedClient.lastContacted) : null
            }));
        } catch (error) {
            console.error("Error refreshing client data:", error);
        }
    };

    useEffect(() => {
        if (onboardingProgress !== "0%") {
            refreshClientData();
        }
    }, [onboardingProgress]);

    if (!client) {
        return <div>Loading client details...</div>;
    }

    const getStatus = (score) => {
        if (score >= 0 && score <= 3.99) return { color: "at-risk", label: "At Risk" };
        if (score >= 4.0 && score <= 6.99) return { color: "fair", label: "Fair" };
        if (score >= 7.0 && score <= 10.0) return { color: "good", label: "Good" };
        return { color: "unknown", label: "Unknown" };
    };

    const getRetentionScoreColor = (score) => {
        if (score >= 0 && score <= 3.99) return '#F52B53'; 
        if (score >= 4.0 && score <= 6.99) return '#F6A637'; 
        if (score >= 7.0 && score <= 10.0) return '#03BC72'; 
        return '#000000'; 
    };

    const calculateColorMain = (value) => {
        if (value >= 7) return { label: 'Great', color: '#03BC72' }; // Green
        if (value >= 5) return { label: 'Good', color: '#F6A637' }; // Orange
        return { label: 'At Risk', color: '#F52B53' }; // Red
    };

    const adjustedScore = (() => {
        const score = formData.clientRetentionScore;
        if (score >= 10) return 10;
        if (score >= 7.5) return 7.5;
        if (score >= 5) return 5;
        if (score >= 2.5) return 2.5;
        return 0; // Default fallback if score is below 2.5
    })();

    const retentionData = calculateColorMain(adjustedScore);

    const status = getStatus(formData.clientRetentionScore);

    const handleEditButtonClick = () => {
        navigate("/edit-client-form", {
            state: {
                clientId: client.id,
                workspaceId: workspaceId,
                clientData: client
            }
        });
    };

    const handleAdminPanelClick = () => {
        navigate('/admin-panel');
    };

    const handleInputChange = async (field, value) => {
        try {
            const updatedClientData = {
                ...client,
                [field]: value,
                workspaceId: workspaceId
            };
            
            await axios.put(
                `${API_URL}/clients/clients`,
                updatedClientData,
                {
                    headers: { "Content-Type": "application/json" },
                    params: { workspaceId: workspaceId }
                }
            );

            if (field === 'billingStatus') {
                setTimeout(async () => {
                    await refreshClientData();
                }, 2000);
            } else {
                setClient(prev => ({ ...prev, [field]: value }));
            }
            
        } catch (error) {
            console.error(`Error updating ${field}:`, error.response?.data || error.message);
            alert(`Error: ${error.response?.data?.message || error.message}`);
        }
    };

    const handleSelectChange = (field) => async (event) => {
        const value = event.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));
        await handleInputChange(field, value);
    };

    const handleDateChange = async (field, newDate) => {
        if (!client.id || !newDate || !workspaceId) {
            console.error("Client ID, workspace ID or new date is missing");
            return;
        }
        
        const isoDate = newDate.toISOString();
        setFormData(prev => ({ ...prev, [field]: newDate }));
        
        try {
            const updatedClientData = {
                ...client,
                [field]: isoDate,
                workspaceId: workspaceId
            };
            
            await axios.put(
                `${API_URL}/clients/clients`,
                updatedClientData,
                {
                    headers: { "Content-Type": "application/json" },
                    params: { workspaceId: workspaceId }
                }
            );

            setTimeout(async () => {
                await refreshClientData();
            }, 1000);
            
        } catch (error) {
            console.error("Error updating last contacted date:", error.response?.data || error.message);
            alert(`Error: ${error.response?.data?.message || error.message}`);
        }
    };

    const getBillingColor = (status) => {
        switch (status) {
            case 'Active':
                return { backgroundColor: '#0AC67D1F', textColor: '#03BC72' };
            case 'Paused':
                return { backgroundColor: '#F4C1071F', textColor: '#F6A637' };
            case 'Past Due':
                return { backgroundColor: '#FF00001F', textColor: '#F52B53' };
            default:
                return { backgroundColor: '#FFFFFF', textColor: '#000000' };
        }
    };

    const { backgroundColor, textColor } = getBillingColor(formData.billingStatus);

    return (
        <div className={`client-details-card-main`}>
            <Button
                className="purple-button"
                sx={{
                    "&:hover": {
                        backgroundColor: "#4A21D1",
                    },
                }}
                style={{
                    position: "absolute",
                    top: "34px",
                    right: "35px",
                }}
                onClick={handleEditButtonClick}
            >
                Edit
            </Button>
            <div
                style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                }}
            >
                <div>
                    <h1>Client Details</h1>
                </div>
            </div>
            <div className="client-name">
                <div className="client-name">
                    {`${formData.firstName || "Unknown"} ${formData.lastName || ""}`.trim()}
                    <span className={`status ${status.color}`} style={{ backgroundColor: 'transparent', padding: 0, marginTop: 0, marginLeft: '10px' }}>
                        &#9679; {status.label}
                    </span>
                </div>
                {formData.companyName && (
                    <div className="client-company-name">
                        @{formData.companyName}
                    </div>
                )}

                <div className="client-usertag">
                    {formData.userTag}
                </div>
            </div>
            
            {/* Retention Score Chart */}
            <div className="retention-chart-main">
                <CircularProgressbarWithChildren
                    value={formData.clientRetentionScore}
                    maxValue={10}
                    circleRatio={0.5}
                    text={`${formData.clientRetentionScore !== null && formData.clientRetentionScore !== undefined ? formData.clientRetentionScore.toFixed(2) : "0.00"}`}
                    styles={buildStyles({
                        textColor: '#291B3B',
                        textSize: '24px',
                        pathColor: calculateColorMain(formData.clientRetentionScore).color,
                        trailColor: '#EDECFD',
                        strokeLinecap: 'butt',
                        rotation: 0.75,
                    })}
                >
                    <RadialSeparators
                        count={4}
                        circleRatio={0.5}
                        style={{
                            background: "#fff",
                            width: "2px",
                            height: "10%",
                        }}
                    />
                </CircularProgressbarWithChildren>
                <div className="retention-label-main">
                    <span className="retention-score-text">Client Retention Score</span>
                </div>
            </div>
            {isEditing && (
                <ClientEditCard
                    client={formData}
                    onSave={(updatedClient) => {
                        setFormData(updatedClient);
                        setClient(updatedClient);
                    }}
                    onClose={() => setIsEditing(false)}
                />
            )}
            <div className="ICP-container" style={{ display: "flex", gap: "10px", marginBottom: "24px" }}>
                <div style={{ flex: 0.8 }}>
                    <h2 style={{ marginBottom: "15px" }}>ICP Status</h2>
                    <FormControl
                        className="icp-dropdown"
                        sx={{
                            width: "100%",
                            backgroundColor: "#0AC67D1F",
                            borderRadius: "6.7px",
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "20px",
                                "& fieldset": {
                                    border: "none",
                                },
                            },
                        }}
                    >
                        <Select
                            value={formData.icp || ""}
                            onChange={handleSelectChange('icp')}
                            label="Status"
                            sx={{
                                "& .MuiSelect-icon": { color: "#000" },
                                "& .MuiSelect-select": {
                                    fontSize: "22px",
                                    textAlign: "left",
                                    paddingLeft: "16px",
                                },
                            }}
                        >
                            <MenuItem value="ICP I">ICP I</MenuItem>
                            <MenuItem value="ICP II">ICP II</MenuItem>
                            <MenuItem value="ICP III">ICP III</MenuItem>
                        </Select>
                    </FormControl>
                </div>
                <div style={{ flex: 1 }}>
                    <h2 style={{ marginBottom: "15px" }}>Touchpoint Frequency</h2>
                    <FormControl
                        className="touchpoint-dropdown"
                        sx={{
                            width: "100%",
                            backgroundColor: "#F4C1071F",
                            borderRadius: "6.7px",
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "20px",
                                "& fieldset": {
                                    border: "none",
                                },
                            },
                        }}
                    >
                        <Select
                            value={normalizeTouchpointFrequency(formData.touchpointFrequency) || ""}
                            onChange={handleSelectChange('touchpointFrequency')}
                            label="Frequency"
                            sx={{
                                "& .MuiSelect-icon": { color: "#000" },
                                "& .MuiSelect-select": {
                                    fontSize: "22px",
                                    textAlign: "left",
                                    paddingLeft: "16px",
                                },
                            }}
                        >
                            <MenuItem value="Weekly">Weekly</MenuItem>
                            <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                            <MenuItem value="Monthly">Monthly</MenuItem>
                        </Select>
                    </FormControl>
                </div>
                <div style={{ flex: 1 }}>
                    <h2 style={{ marginBottom: "15px" }}>Billing Status</h2>
                    <FormControl
                        className="billing-status-dropdown"
                        sx={{
                            width: "80%",
                            backgroundColor: backgroundColor, 
                            borderRadius: "6.7px",
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "20px",
                                "& fieldset": {
                                    border: "none",
                                },
                            },
                        }}
                    >
                        <Select
                            value={formData.billingStatus || ""}
                            onChange={handleSelectChange('billingStatus')}
                            label="Billing Status"
                            sx={{
                                color: textColor, 
                                "& .MuiSelect-icon": { color: "#000" },
                                "& .MuiSelect-select": {
                                    fontSize: "22px",
                                    textAlign: "left",
                                    paddingLeft: "16px",
                                },
                            }}
                        >
                            <MenuItem value="Active" style={{ color: '#03BC72' }}>Active</MenuItem>
                            <MenuItem value="Paused" style={{ color: '#F6A637' }}>Paused</MenuItem>
                            <MenuItem value="Past Due" style={{ color: '#F52B53' }}>Past Due</MenuItem>
                        </Select>
                    </FormControl>
                </div>
            </div>
            <div style={{ marginBottom: 5 }}>
                <FormControl sx={{ width: '100%' }}>
                    {/* Service Type label */}
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        Program / Service Type
                        <InfoTooltip
                            title={
                                <span style={{ fontSize: "1.2rem", lineHeight: 1.5, color: "#fff" }}>
                                    To add a new program,<br />
                                    go to the Business Profile Configuration section in Admin<br />
                                    and use the + Add Program button.
                                </span>
                            }
                            placement="top"
                            iconSx={{ color: '#888', fontSize: 16, opacity: 0.7 }}
                        />
                    </span>
                    <Select
                        multiple
                        value={selectedPrograms}
                        onChange={async (event) => {
                            const value = event.target.value;
                            const added = value.find(id => !selectedPrograms.includes(id));
                            if (added) {
                                try {
                                    await axios.post(`${API_URL}/programs/assign-program`, {
                                        workspaceId: Number(workspaceId),
                                        userId: Number(userId),
                                        programId: Number(added),
                                        clientId: Number(clientId)
                                    });
                                    setSelectedPrograms([...selectedPrograms, added]);
                                    toast.success('Program assigned successfully!');
                                } catch (err) {
                                    alert('Error assigning program');
                                }
                            }
                        }}
                        displayEmpty
                        renderValue={() =>
                            selectedPrograms.length > 0
                                ? (
                                    <span style={{ opacity: 0.7 }}>
                                        {allPrograms
                                            .filter(p => selectedPrograms.includes(p.id))
                                            .map(p => p.description)
                                            .join(', ')}
                                    </span>
                                )
                                : "Program / Service Type"
                        }
                        sx={{ backgroundColor: "#f5f5f5" }}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 10 * 48, // 10 items aprox
                                },
                            },
                        }}
                    >
                        <MenuItem disabled value="">
                            Program / Service Type
                        </MenuItem>
                        {[
                            ...allPrograms.filter(p => selectedPrograms.includes(p.id)),
                            ...allPrograms.filter(p => !selectedPrograms.includes(p.id))
                        ].map((program) => {
                            const isSelected = selectedPrograms.includes(program.id);
                            return (
                                <MenuItem
                                    key={program.id}
                                    value={program.id}
                                    style={{ display: 'flex', alignItems: 'center', opacity: isSelected ? 1 : 0.8 }}
                                    onClick={isSelected ? (e) => e.preventDefault() : undefined}
                                >
                                    {isSelected && (
                                        <CheckCircleIcon style={{ color: '#03BC72', marginRight: 8 }} fontSize="small" />
                                    )}
                                    <span style={{ flex: 1 }}>{program.description}</span>
                                    {isSelected && (
                                        <span
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginLeft: 8,
                                                borderRadius: '50%',
                                                transition: 'background 0.2s',
                                                cursor: 'pointer',
                                                width: 24,
                                                height: 24,
                                            }}
                                            onMouseEnter={e => {
                                                e.currentTarget.style.background = '#ffeaea';
                                            }}
                                            onMouseLeave={e => {
                                                e.currentTarget.style.background = 'transparent';
                                            }}
                                            onClick={e => {
                                                e.stopPropagation();
                                                setProgramToRemove(program.id);
                                                setOpenRemoveModal(true);
                                            }}
                                        >
                                            <CloseIcon
                                                style={{ color: '#F52B53' }}
                                                fontSize="small"
                                            />
                                        </span>
                                    )}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </div>
            <Dialog open={openRemoveModal} onClose={() => setOpenRemoveModal(false)}>
                <DialogTitle>Remove Program</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove this program from the client?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenRemoveModal(false)}>Cancel</Button>
                    <Button onClick={async () => {
                        await handleRemoveProgram(programToRemove);
                        setOpenRemoveModal(false);
                        setProgramToRemove(null);
                    }} color="error">Remove</Button>
                </DialogActions>
            </Dialog>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
                    <div className="date-fields-section">
                     <DateField
                        label="Last Contacted On"
                        value={formData.lastContacted}
                        onChange={(newDate) => handleDateChange('lastContacted', newDate)}
                        />
                        
                        <DateField
                        label="Last Call Attended"
                        value={formData.lastCallAttended}
                        onChange={(newDate) => handleDateChange('lastCallAttended', newDate)}
                        />
                        
                        <DateField
                        label="Last In-Person"
                        caption="This could mean Last In-Person Event, Last Service Rendered, or Last In-Person Visit depending on industry."
                        value={formData.lastInPersonEvent}
                        onChange={(newDate) => handleDateChange('lastInPersonEvent', newDate)}
                     />
                    </div>
            </div>
            <div className="contact-info">
                <div>
                    <div className="attribute">
                        <span className="attribute-in" style={{ position: 'relative', display: 'inline-block' }}>
                            Onboarding Date:
                            <InfoTooltip
                                title={
                                <span style={{ fontSize: "1.2rem", lineHeight: 1.5, color: "#fff" }}>
                                    To adjust onboarding date,<br />
                                    click the 'Edit' button at the<br />
                                    top of this section
                                </span>
                                }
                                placement="top"
                                iconSx={{ color: '#888', fontSize: 16, opacity: 0.7, position: 'absolute', right: -5, top: '-18px' }}
                            />
                        </span>
                        <span className="contact-attribute">
                            {formatDate(formData.onboardingDate)}
                        </span>
                    </div>
                    <div className="attribute">
                        <span className="attribute-in">Email:</span>{" "}
                        <span className="contact-attribute">{formData.email}</span>
                    </div>
                    <div className="attribute">
                        <span className="attribute-in">Phone:</span>{" "}
                        <span className="contact-attribute">{formData.phoneNumber}</span>
                    </div>
                    <div className="attribute">
                        <span className="attribute-in">Address:</span>{" "}
                        <span className="contact-attribute">{formData.address}</span>
                    </div>
                </div>
            </div>
            <div className="metrics-container">
                {isMetricsLoading ? (
                    // Skeleton loading para las métricas
                    <>
                        <div className="metrics-card skeleton-loading">
                            <div className="skeleton-h2"></div>
                            <div className="skeleton-p"></div>
                        </div>
                        <div className="metrics-card skeleton-loading">
                            <div className="skeleton-h2"></div>
                            <div className="skeleton-p"></div>
                        </div>
                        <div className="metrics-card skeleton-loading">
                            <div className="skeleton-h2"></div>
                            <div className="skeleton-p"></div>
                        </div>
                        <div className="metrics-card skeleton-loading">
                            <div className="skeleton-h2"></div>
                            <div className="skeleton-p"></div>
                        </div>
                        <div className="metrics-card skeleton-loading">
                            <div className="skeleton-h2"></div>
                            <div className="skeleton-p"></div>
                        </div>
                        <div className="metrics-card skeleton-loading">
                            <div className="skeleton-h2"></div>
                            <div className="skeleton-p"></div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="metrics-card">
                            <h2>{formData.netPromoterScore !== undefined && formData.netPromoterScore !== "" ? formData.netPromoterScore : "0"}</h2>
                            <p>Net Promoter Score (NPS)</p>
                        </div>
                        <div className="metrics-card">
                            <h2>{formData.clientEffortScores !== undefined && formData.clientEffortScores !== "" ? formData.clientEffortScores : "0"}</h2>
                            <p>Client Effort Scores (CES)</p>
                        </div>
                        {circleConnected ? (
                            <div className="metrics-card">
                                <h2>{formData.activityScore !== undefined && formData.activityScore !== null ? formData.activityScore : 'N/A'}</h2>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <p>Circle Activity Score</p>
                                    {isAdmin && (
                                        <InfoTooltip
                                            title={
                                                <span style={{ fontSize: "1.2rem", lineHeight: 1.5, color: "#fff" }}>
                                                    This metric is imported for circle.so users. (optional 3rd party software).<br /><br />
                                                    To learn more about this online community building tool and how impacts your CRS.{" "}
                                                    <span 
                                                        style={{ 
                                                            color: "#87C4FF", 
                                                            cursor: "pointer", 
                                                            textDecoration: "underline" 
                                                        }}
                                                        onClick={handleAdminPanelClick}
                                                    >
                                                        Click Here
                                                    </span>
                                                </span>
                                            }
                                            placement="top"
                                            iconSx={{ color: '#888', fontSize: 14, opacity: 0.7 }}
                                        />
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="metrics-card">
                                <h2>${formData.lifeTimeValue !== undefined && formData.lifeTimeValue !== "" ? formData.lifeTimeValue : "0"}</h2>
                                <p>Life Time Value (LTV)</p>
                            </div>
                        )}
                        {circleConnected && (
                            <div className="metrics-card">
                                <h2>${formData.lifeTimeValue !== undefined && formData.lifeTimeValue !== "" ? formData.lifeTimeValue : "0"}</h2>
                                <p>Life Time Value (LTV)</p>
                            </div>
                        )}
                        <div className="metrics-card">
                            <h2>
                                {formData.monthlyFeeSubscription !== undefined && formData.monthlyFeeSubscription !== "" && formData.monthlyFeeSubscription !== null
                                    ? `$${formData.monthlyFeeSubscription}`
                                    : "$0"}
                            </h2>
                            <p>Monthly Fee/ Subscription</p>
                        </div>
                        <div className="metrics-card">
                            <h2>
                                {formData.oneTimeOnboardingFee !== undefined && formData.oneTimeOnboardingFee !== "" && formData.oneTimeOnboardingFee !== null
                                    ? `$${formData.oneTimeOnboardingFee}`
                                    : "$0"}
                            </h2>
                            <p>One-Time Onboarding Fee
                            (Leave Blank if Not Applicable)</p>
                        </div>
                    </>
                )}
            </div>
            <OnboardingFlow
                clientId={client.id}
                setOnboardingProgress={setOnboardingProgress}
            />
        </div>
    );
};

export default ClientDetailMain;